import type React from "react";
import { Box, Text, Group } from "@mantine/core";
import { Badge, Card } from "@xplatform/design-system";
import { IconArrowRight } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { MappingServiceViewModel } from "@models/service";
import "./MappingServiceCard.scss";

export interface MappingServiceCardProps {
  service: MappingServiceViewModel;
  onSelect: (serviceId: string) => void;
}

/**
 * Humanize a kebab-case identifier so unknown / unregistered
 * services still render a readable fallback title without crashing
 * on a missing i18n key.
 */
const humanize = (value: string): string =>
  value
    .split(/[-_]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");

const MappingServiceCard: React.FC<MappingServiceCardProps> = ({
  service,
  onSelect,
}) => {
  const { t } = useTranslation();

  const descriptionKey = `mappingServices.${service.kind}.${service.pluginId}.description`;
  const kindLabelKey = `mappingServices.kinds.${service.kind}`;

  const translatedDescription = t(descriptionKey, { defaultValue: "" });
  const translatedKind = t(kindLabelKey, { defaultValue: "" });

  const fallbackDescription = t("mappingServices.fallback.description", {
    id: service.id,
    defaultValue: service.id,
  });
  const fallbackKindLabel = humanize(service.kind);

  const displayTitle = translatedKind || fallbackKindLabel;
  const displayDescription = translatedDescription || fallbackDescription;
  const displayBadge = service.pluginId;

  const handleSelect = (): void => {
    onSelect(service.id);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect(service.id);
    }
  };

  return (
    <Card
      className="mapping-service-card"
      shadow="sm"
      padding="lg"
      radius="md"
      role="button"
      tabIndex={0}
      aria-label={t("mappingServices.card.ariaLabel", {
        title: displayTitle,
        defaultValue: `Open mapping service ${displayTitle}`,
      })}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Badge color="brand" variant="light" size="sm">
          {displayBadge}
        </Badge>
        <IconArrowRight
          size={18}
          stroke={1.5}
          className="mapping-service-card__arrow"
          aria-hidden
        />
      </Group>
      <Text
        size="lg"
        fw={600}
        mt="sm"
        component="h3"
        className="mapping-service-card__title"
      >
        {displayTitle}
      </Text>
      <Text size="sm" c="dimmed" mt="xs" className="mapping-service-card__description">
        {displayDescription}
      </Text>
      <Box className="mapping-service-card__meta" aria-hidden>
        <Text size="xs" c="dimmed">
          {service.id}
        </Text>
      </Box>
    </Card>
  );
};

export default MappingServiceCard;
